/**
 * @fileoverview Height update utilities with debouncing and DOM manipulation
 * @module HeightUpdater
 */

import { HEIGHTS, CONFIG } from './heightConstants.js';
import { cacheGanttContainers, getCachedParentContainer } from './domCache.js';
import { sentryLogger } from '../../../monitor/monitor.js';

let updateTimeout = null;

/**
 * Calculate target height based on current state
 * @param {boolean} isBABOpen - Whether BAB is visible
 * @param {boolean} isActivityCardOpen - Whether drawer is open
 * @returns {string} CSS height value
 */
export function calculateTargetHeight(isBABOpen, isActivityCardOpen) {
  if (isBABOpen && isActivityCardOpen) {
    return HEIGHTS.WITH_BOTH;
  } else if (isBABOpen) {
    return HEIGHTS.WITH_BAB;
  } else if (isActivityCardOpen) {
    return HEIGHTS.WITH_ACTIVITY_CARD;
  } else {
    return HEIGHTS.DEFAULT;
  }
}

/**
 * Update gantt container height with debouncing
 * @param {string} targetHeight - Target CSS height value
 * @param {string} lastHeight - Last applied height value
 * @returns {Promise<string>} Promise resolving to applied height
 */
export function updateGanttHeight(targetHeight, lastHeight) {
  // Clear any pending update
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }

  // Debounce updates
  return new Promise((resolve) => {
    updateTimeout = setTimeout(() => {
      const ganttContainer = cacheGanttContainers();

      if (!ganttContainer) {
        sentryLogger.warn('Gantt container not found', {
          entityName: 'GanttHeightManager',
          context: 'updateGanttHeight'
        });
        resolve(lastHeight);
        return;
      }

      // Performance: Only update if height actually changed
      if (lastHeight === targetHeight) {
        resolve(lastHeight);
        return;
      }

      // Batch DOM writes
      requestAnimationFrame(() => {
        ganttContainer.style.height = targetHeight;
        const cachedParent = getCachedParentContainer();
        if (cachedParent) {
          cachedParent.style.height = targetHeight;
        }

        // Trigger render if available (with error handling)
        if (window.to_use_react_gantt?.render) {
          try {
            window.to_use_react_gantt.render();
          } catch (error) {
            sentryLogger.error('Gantt render failed', {
              entityName: 'GanttHeightManager',
              context: 'updateGanttHeight',
              error
            });
          }
        }

        resolve(targetHeight);
      });
    }, CONFIG.DEBOUNCE_DELAY);
  });
}

/**
 * Clear update timeout (for cleanup)
 */
export function clearUpdateTimeout() {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
}
